import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "../../lib/api";
import { toast } from "sonner";
import { PageHeader, SectionPad, Badge } from "../../components/ui-extras";
import { ArrowLeft, PencilSimple, Trash, Phone, FloppyDisk, Rocket, ChartLine } from "@phosphor-icons/react";

const CALL_OUTCOMES = [
  { value: "voicemail",       label: "Voicemail" },
  { value: "spoke",           label: "Spoke" },
  { value: "follow_up",       label: "Follow-up" },
  { value: "not_interested",  label: "Not interested" },
  { value: "quote_sent",      label: "Quote sent" },
  { value: "other",           label: "Other" },
];

const STATUSES = ["lead", "active", "dormant", "lost", "onboarded"];

/**
 * Prospect detail — single-pane view with three sections:
 *   1. Editable profile (company/contact/phone/etc.)
 *   2. Call log (add/edit/delete)
 *   3. Linked competitive analyses (24h TTL)
 * Plus an "Onboard" button that opens a prefilled New-Customer modal.
 */
export default function ProspectDetail() {
  const { prospectId } = useParams();
  const nav = useNavigate();
  const [prospect, setProspect] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [calls, setCalls] = useState([]);
  const [analyses, setAnalyses] = useState([]);
  const [addingCall, setAddingCall] = useState(false);
  const [editingCall, setEditingCall] = useState(null);   // call being edited
  const [callForm, setCallForm] = useState({ outcome: "spoke", note: "", next_action_date: "" });
  const [showOnboard, setShowOnboard] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, c, a] = await Promise.all([
        api.get(`/prospects/${prospectId}`),
        api.get(`/prospects/${prospectId}/calls`),
        api.get(`/prospects/${prospectId}/analyses`),
      ]);
      setProspect(p.data);
      setForm(p.data);
      setCalls(c.data.calls || []);
      setAnalyses(a.data.analyses || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load prospect");
    }
  }, [prospectId]);

  useEffect(() => { load(); }, [load]);

  const saveProfile = async () => {
    setSaving(true);
    try {
      // Send only the fields ProspectUpdate accepts (Pydantic extra='forbid').
      const allowed = ["company_name", "contact_name", "contact_email", "phone",
                       "address", "city", "province", "status",
                       "expected_monthly_volume_l", "assigned_to", "notes"];
      const body = {};
      for (const k of allowed) {
        if (form[k] !== undefined) body[k] = form[k];
      }
      if (body.expected_monthly_volume_l === "") body.expected_monthly_volume_l = null;
      const { data } = await api.put(`/prospects/${prospectId}`, body);
      setProspect(data);
      setEditing(false);
      toast.success("Saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const saveCall = async () => {
    if (!callForm.outcome) return;
    try {
      if (editingCall) {
        const body = { outcome: callForm.outcome, note: callForm.note, next_action_date: callForm.next_action_date || null };
        await api.put(`/prospects/${prospectId}/calls/${editingCall.call_id}`, body);
        toast.success("Call updated");
      } else {
        await api.post(`/prospects/${prospectId}/calls`, callForm);
        toast.success("Call logged");
      }
      setAddingCall(false); setEditingCall(null);
      setCallForm({ outcome: "spoke", note: "", next_action_date: "" });
      const c = await api.get(`/prospects/${prospectId}/calls`);
      setCalls(c.data.calls || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const delCall = async (callId) => {
    if (!window.confirm("Delete this call entry?")) return;
    await api.delete(`/prospects/${prospectId}/calls/${callId}`);
    toast.success("Deleted");
    const c = await api.get(`/prospects/${prospectId}/calls`);
    setCalls(c.data.calls || []);
  };

  if (!prospect) return <SectionPad><div className="text-zinc-400 italic">Loading…</div></SectionPad>;

  return (
    <SectionPad>
      <PageHeader
        title={prospect.company_name}
        subtitle={
          <Link to="/sales-tools/crm" className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1">
            <ArrowLeft size={12}/> Prospects
          </Link>
        }
        actions={
          <>
            <Badge variant={prospect.status === "onboarded" ? "success" : prospect.status === "lost" ? "danger" : "muted"}>
              {prospect.status}
            </Badge>
            {prospect.onboarded_customer_id ? (
              <button
                className="gf-btn gf-btn-outline"
                onClick={() => nav(`/customers/${prospect.onboarded_customer_id}`)}
                data-testid="open-customer-button"
              >
                Open customer
              </button>
            ) : (
              <button
                className="gf-btn gf-btn-primary"
                onClick={() => setShowOnboard(true)}
                data-testid="onboard-button"
              >
                <Rocket size={14}/> Onboard
              </button>
            )}
          </>
        }
      />

      {/* ---- PROFILE ---- */}
      <div className="gf-card mb-4">
        <div className="gf-card-header flex items-center justify-between">
          <h3>Profile</h3>
          {!editing ? (
            <button className="gf-btn gf-btn-ghost" onClick={() => { setForm(prospect); setEditing(true); }} data-testid="edit-profile-button">
              <PencilSimple size={14}/> Edit
            </button>
          ) : (
            <div className="flex gap-2">
              <button className="gf-btn gf-btn-outline" onClick={() => { setForm(prospect); setEditing(false); }} disabled={saving}>Cancel</button>
              <button className="gf-btn gf-btn-primary" onClick={saveProfile} disabled={saving} data-testid="save-profile-button"><FloppyDisk size={14}/> Save</button>
            </div>
          )}
        </div>
        <div className="gf-card-body">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              ["company_name",             "COMPANY NAME"],
              ["contact_name",             "CONTACT NAME"],
              ["contact_email",            "EMAIL"],
              ["phone",                    "PHONE"],
              ["address",                  "ADDRESS"],
              ["city",                     "CITY"],
              ["province",                 "PROVINCE"],
              ["expected_monthly_volume_l","EXP. VOLUME / MO (L)"],
              ["assigned_to",              "ASSIGNED TO (USER ID)"],
            ].map(([key, label]) => (
              <div key={key}>
                <div className="overline mb-1">{label}</div>
                {editing ? (
                  <input
                    type={key === "expected_monthly_volume_l" ? "number" : "text"}
                    className="gf-input"
                    value={form[key] ?? ""}
                    onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                  />
                ) : (
                  <div className="text-sm font-mono">{prospect[key] != null && prospect[key] !== "" ? String(prospect[key]) : <span className="text-zinc-400">—</span>}</div>
                )}
              </div>
            ))}
            <div>
              <div className="overline mb-1">STATUS</div>
              {editing ? (
                <select className="gf-input" value={form.status || "lead"} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              ) : (
                <Badge variant={prospect.status === "onboarded" ? "success" : "muted"}>{prospect.status}</Badge>
              )}
            </div>
            <div className="md:col-span-3">
              <div className="overline mb-1">NOTES</div>
              {editing ? (
                <textarea className="gf-input" rows={3} value={form.notes ?? ""} onChange={(e) => setForm({ ...form, notes: e.target.value })}/>
              ) : (
                <div className="text-sm whitespace-pre-wrap">{prospect.notes || <span className="text-zinc-400">—</span>}</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ---- CALL LOG ---- */}
      <div className="gf-card mb-4">
        <div className="gf-card-header flex items-center justify-between">
          <h3>Call log ({calls.length})</h3>
          {!addingCall && !editingCall && (
            <button className="gf-btn gf-btn-primary" onClick={() => { setAddingCall(true); setCallForm({ outcome: "spoke", note: "", next_action_date: "" }); }} data-testid="add-call-button">
              <Phone size={14}/> Log call
            </button>
          )}
        </div>
        <div className="gf-card-body">
          {(addingCall || editingCall) && (
            <div className="border border-zinc-200 p-3 mb-3 bg-zinc-50">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <div className="overline mb-1">OUTCOME</div>
                  <select className="gf-input" value={callForm.outcome} onChange={(e) => setCallForm({ ...callForm, outcome: e.target.value })} data-testid="call-outcome">
                    {CALL_OUTCOMES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <div className="overline mb-1">NEXT ACTION DATE</div>
                  <input type="date" className="gf-input" value={callForm.next_action_date || ""} onChange={(e) => setCallForm({ ...callForm, next_action_date: e.target.value })}/>
                </div>
              </div>
              <div className="mt-3">
                <div className="overline mb-1">NOTE</div>
                <textarea className="gf-input" rows={3} value={callForm.note} onChange={(e) => setCallForm({ ...callForm, note: e.target.value })} data-testid="call-note" placeholder="What did you discuss? Objections? Next steps?"/>
              </div>
              <div className="flex justify-end gap-2 mt-3">
                <button className="gf-btn gf-btn-outline" onClick={() => { setAddingCall(false); setEditingCall(null); }}>Cancel</button>
                <button className="gf-btn gf-btn-primary" onClick={saveCall} data-testid="save-call-button">{editingCall ? "Update" : "Log call"}</button>
              </div>
            </div>
          )}
          {calls.length === 0 && !addingCall && (
            <div className="text-sm text-zinc-400 italic">No calls logged yet.</div>
          )}
          <ul className="space-y-3">
            {calls.map((c) => (
              <li key={c.call_id} className="border border-zinc-200 p-3" data-testid={`call-row-${c.call_id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 text-xs font-mono text-zinc-500 mb-1">
                      <span>{new Date(c.logged_at).toLocaleString()}</span>
                      <span>·</span>
                      <Badge variant="muted">{c.outcome}</Badge>
                      {c.next_action_date && <><span>·</span><span className="text-amber-700">NEXT {c.next_action_date}</span></>}
                    </div>
                    <div className="text-sm whitespace-pre-wrap">{c.note || <span className="text-zinc-400 italic">No note.</span>}</div>
                    <div className="text-[11px] text-zinc-400 mt-1">by {c.caller_name || c.caller}</div>
                  </div>
                  <div className="flex gap-1">
                    <button className="gf-btn gf-btn-ghost" onClick={() => { setEditingCall(c); setCallForm({ outcome: c.outcome, note: c.note || "", next_action_date: c.next_action_date || "" }); }} title="Edit"><PencilSimple size={14}/></button>
                    <button className="gf-btn gf-btn-ghost" onClick={() => delCall(c.call_id)} title="Delete"><Trash size={14}/></button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* ---- LINKED ANALYSES ---- */}
      <div className="gf-card mb-4">
        <div className="gf-card-header flex items-center justify-between">
          <h3>Linked analyses ({analyses.length})</h3>
          <Link to="/sales-tools/analyzer" className="text-xs text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1">
            <ChartLine size={12}/> Open analyzer
          </Link>
        </div>
        <div className="gf-card-body">
          {analyses.length === 0 ? (
            <div className="text-sm text-zinc-400 italic">
              No analyses yet. In the <Link to="/sales-tools/analyzer" className="underline">Price Analyzer</Link>, set "Customer label" to <b className="font-mono">{prospect.company_name}</b> when uploading — it'll show up here.
            </div>
          ) : (
            <table className="gf-table text-xs">
              <thead><tr>
                <th>Created</th><th>File</th><th>Mode</th><th>Network</th>
                <th className="text-right">Ann. savings</th>
                <th>Expires</th>
              </tr></thead>
              <tbody>
                {analyses.map((a) => (
                  <tr key={a.analysis_id}>
                    <td className="font-mono">{(a.created_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="truncate max-w-[280px]">{a.filename}</td>
                    <td><Badge variant="muted">{a.mode}</Badge></td>
                    <td className="font-mono">{a.network || "any"}</td>
                    <td className={`text-right font-mono ${(a.summary?.annualised_savings || 0) > 0 ? "text-emerald-700" : (a.summary?.annualised_savings || 0) < 0 ? "text-red-700" : ""}`}>
                      {a.summary?.annualised_savings != null
                        ? `$${a.summary.annualised_savings >= 0 ? "+" : ""}${Number(a.summary.annualised_savings).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                        : "—"}
                    </td>
                    <td className="font-mono text-[11px] text-zinc-400">{(a.expires_at || "").slice(0, 16).replace("T", " ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ---- ONBOARD MODAL ---- */}
      {showOnboard && (
        <OnboardModal
          prospect={prospect}
          onClose={() => setShowOnboard(false)}
          onDone={(customer_id) => {
            toast.success("Customer created — opening profile…");
            setShowOnboard(false);
            nav(`/customers/${customer_id}`);
          }}
        />
      )}
    </SectionPad>
  );
}

function OnboardModal({ prospect, onClose, onDone }) {
  // Prefill every CustomerIn field from the prospect; user can tweak anything
  // before the customer is actually created. This implements the user-chosen
  // "two-step" onboard flow.
  const [form, setForm] = useState({
    company_name: prospect.company_name || "",
    contact_name: prospect.contact_name || "",
    contact_email: prospect.contact_email || "",
    phone: prospect.phone || "",
    address: prospect.address || "",
    province: prospect.province || "",
    billing_email: prospect.contact_email || "",
    tax_rate: 0.13,
    credit_limit: 0,
    customer_type: "postpaid",
    currency: "CAD",
    tax_id: "",
  });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      // Send as OnboardIn overrides; backend builds a full customer doc.
      const payload = { ...form };
      payload.tax_rate = parseFloat(payload.tax_rate) || 0;
      payload.credit_limit = parseFloat(payload.credit_limit) || 0;
      const { data } = await api.post(`/prospects/${prospect.prospect_id}/onboard`, payload);
      onDone(data.customer.customer_id);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (detail?.message || "Onboard failed");
      toast.error(msg);
      setSaving(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-4" onClick={() => !saving && onClose()}>
      <div className="bg-white w-full max-w-2xl border border-zinc-200 flex flex-col max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
        <div className="gf-card-header"><h3>Onboard prospect → new customer</h3></div>
        <div className="gf-card-body overflow-y-auto flex-1 space-y-4">
          <div className="text-xs text-zinc-500">
            Review and tweak before we create the customer. Profile fields come from
            the prospect; billing fields default to sensible values.
          </div>
          <div>
            <div className="overline mb-2">PROFILE (copied from prospect)</div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <div className="overline mb-1">COMPANY NAME *</div>
                <input className="gf-input" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="onboard-company-name"/>
              </div>
              <div><div className="overline mb-1">CONTACT NAME</div><input className="gf-input" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })}/></div>
              <div><div className="overline mb-1">PHONE</div><input className="gf-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}/></div>
              <div><div className="overline mb-1">EMAIL</div><input className="gf-input" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })}/></div>
              <div><div className="overline mb-1">BILLING EMAIL</div><input className="gf-input" value={form.billing_email} onChange={(e) => setForm({ ...form, billing_email: e.target.value })}/></div>
              <div className="col-span-2"><div className="overline mb-1">ADDRESS</div><input className="gf-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}/></div>
              <div><div className="overline mb-1">PROVINCE</div><input className="gf-input" value={form.province} maxLength={2} onChange={(e) => setForm({ ...form, province: e.target.value.toUpperCase() })}/></div>
            </div>
          </div>
          <div>
            <div className="overline mb-2">BILLING DEFAULTS (tweak anytime later too)</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="overline mb-1">CUSTOMER TYPE</div>
                <select className="gf-input" value={form.customer_type} onChange={(e) => setForm({ ...form, customer_type: e.target.value })}>
                  <option value="postpaid">postpaid</option>
                  <option value="prepaid">prepaid</option>
                </select>
              </div>
              <div>
                <div className="overline mb-1">TAX RATE</div>
                <input type="number" step="0.01" className="gf-input" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: e.target.value })}/>
              </div>
              <div>
                <div className="overline mb-1">CREDIT LIMIT ($)</div>
                <input type="number" className="gf-input" value={form.credit_limit} onChange={(e) => setForm({ ...form, credit_limit: e.target.value })}/>
              </div>
              <div>
                <div className="overline mb-1">TAX ID / HST</div>
                <input className="gf-input" value={form.tax_id} onChange={(e) => setForm({ ...form, tax_id: e.target.value })}/>
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 p-4 border-t border-zinc-200">
          <button className="gf-btn gf-btn-outline" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="gf-btn gf-btn-primary" onClick={submit} disabled={saving || !form.company_name.trim()} data-testid="onboard-confirm-button">
            {saving ? "Creating…" : <><Rocket size={14}/> Create customer</>}
          </button>
        </div>
      </div>
    </div>
  );
}
